import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Phone, X } from 'lucide-react';
import { gsap } from 'gsap';

export const CrisisDrawer: React.FC = () => {
    const [isOpen, setIsOpen] = useState(false);
    const location = useLocation();
    const drawerRef = useRef<HTMLDivElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);
    const overlayRef = useRef<HTMLDivElement>(null);



    // Handle drawer open/close animations
    useEffect(() => {
        if (!drawerRef.current || !overlayRef.current || !contentRef.current) return;

        const drawer = drawerRef.current;
        const overlay = overlayRef.current;
        const content = contentRef.current;

        if (isOpen) {
            // Open animation
            gsap.set(drawer, { x: '100%' });
            gsap.set(overlay, { opacity: 0, pointerEvents: 'auto' });

            gsap.to(overlay, {
                opacity: 1,
                duration: 0.3,
                ease: 'power2.out'
            });

            gsap.to(drawer, {
                x: '0%',
                duration: 0.5,
                ease: 'power3.out'
            });

            // Stagger content animation
            const textElements = content.querySelectorAll('.animate-in');
            gsap.fromTo(textElements,
                { opacity: 0, y: 20 },
                {
                    opacity: 1,
                    y: 0,
                    duration: 0.4,
                    stagger: 0.1,
                    delay: 0.2,
                    ease: 'power2.out'
                }
            );
        } else {
            // Close animation
            gsap.to(drawer, {
                x: '100%',
                duration: 0.4,
                ease: 'power3.in'
            });

            gsap.to(overlay, {
                opacity: 0,
                duration: 0.3,
                ease: 'power2.in',
                onComplete: () => {
                    gsap.set(overlay, { pointerEvents: 'none' });
                }
            });
        }
    }, [isOpen]);

    // Close on escape key
    useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && isOpen) {
                setIsOpen(false);
            }
        };

        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [isOpen]);

    // Hide on contact page
    if (location.pathname === '/contact') {
        return null;
    }

    return (
        <>
            {/* Overlay */}
            <div
                ref={overlayRef}
                className="fixed inset-0 bg-black/50 z-[10000] opacity-0 pointer-events-none backdrop-blur-sm"
                onClick={() => setIsOpen(false)}
                aria-hidden="true"
            />

            {/* Sticky Tab (Trigger) */}
            <button
                onClick={() => setIsOpen(true)}
                className="fixed right-0 top-1/2 -translate-y-1/2 z-[10000] group"
                aria-label="Open Crisis Line information"
                style={{
                    writingMode: 'vertical-rl',
                    textOrientation: 'mixed'
                }}
            >
                <div className="flex items-center gap-3 bg-[#c41e3a] text-white px-3 py-4 md:py-6 rounded-l-lg shadow-2xl transition-all duration-300 group-hover:bg-[#a01830] group-hover:shadow-[0_0_30px_rgba(196,30,58,0.5)] group-hover:pl-4">
                    <Phone className="w-5 h-5 rotate-90" strokeWidth={2} />
                    <span className="font-semibold tracking-wider text-sm uppercase hidden md:block">Crisis Line</span>
                </div>
            </button>

            {/* Drawer Panel */}
            <div
                ref={drawerRef}
                className="fixed right-0 top-0 h-full w-full max-w-md bg-white z-[10001] shadow-2xl transform translate-x-full"
                role="dialog"
                aria-modal="true"
                aria-labelledby="crisis-drawer-title"
            >
                {/* Subtle gradient overlay */}
                <div className="absolute inset-0 bg-gradient-to-b from-neutral-50 to-white pointer-events-none" />

                {/* Close Button */}
                <button
                    onClick={() => setIsOpen(false)}
                    className="absolute top-6 right-6 text-neutral-400 hover:text-neutral-700 transition-colors duration-200 p-2 rounded-full hover:bg-neutral-100"
                    aria-label="Close crisis line drawer"
                >
                    <X className="w-6 h-6" />
                </button>

                {/* Content */}
                <div ref={contentRef} className="flex flex-col justify-center h-full px-10 py-20">
                    {/* Header */}
                    <div className="animate-in mb-10">
                        <div className="flex items-center gap-4 mb-4">
                            <div className="w-14 h-14 rounded-full bg-[#c41e3a] flex items-center justify-center shadow-lg shadow-[#c41e3a]/30">
                                <Phone className="w-7 h-7 text-white" strokeWidth={2.5} />
                            </div>
                            <h2 id="crisis-drawer-title" className="text-3xl font-bold text-[#293039] tracking-tight">
                                Crisis Line
                            </h2>
                        </div>
                        <div className="w-20 h-1 bg-gradient-to-r from-[#c41e3a] to-transparent rounded-full" />
                    </div>

                    {/* Message */}
                    <p className="animate-in text-neutral-600 text-lg leading-relaxed mb-10">
                        If you, or someone you know is in crisis, please seek help immediately.
                    </p>

                    {/* Call Action */}
                    <div className="animate-in mb-8">
                        <p className="text-neutral-500 text-sm uppercase tracking-widest mb-3 font-medium">Call Now</p>
                        <a
                            href="tel:1-844-493-8255"
                            className="group inline-flex items-center gap-4 bg-[#c41e3a] text-white text-xl md:text-2xl font-bold py-4 px-6 md:px-8 rounded-xl shadow-lg shadow-[#c41e3a]/30 hover:shadow-[#c41e3a]/50 hover:bg-[#a01830] hover:scale-[1.02] transition-all duration-300"
                        >
                            <Phone className="w-5 h-5 md:w-6 md:h-6 group-hover:animate-pulse" />
                            <span>1-844-493-8255</span>
                        </a>
                    </div>

                    {/* Text Action */}
                    <div className="animate-in">
                        <p className="text-neutral-500 text-sm uppercase tracking-widest mb-3 font-medium">Or Text</p>
                        <div className="bg-neutral-100 border border-neutral-200 rounded-xl p-6">
                            <p className="text-neutral-700 text-lg">
                                Text the word <span className="font-bold text-[#c41e3a]">TALK</span> to{' '}
                                <span className="font-bold text-[#293039]">38255</span>
                            </p>
                        </div>
                    </div>

                    {/* Footer note */}
                    <p className="animate-in mt-12 text-neutral-400 text-sm">
                        Colorado Crisis Services • Available 24/7
                    </p>
                </div>
            </div>
        </>
    );
};

export default CrisisDrawer;
